
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, Building, Mail, Phone, Globe, MapPin, Clock, Percent, Shield, AlertTriangle, Instagram, Facebook } from 'lucide-react';
import { formatOpeningHours } from '@communitly/shared';
import { PATH_CONSTANTS } from '@/routes/pathConstants';

interface BusinessReviewFormProps {
  formData: any;
  categories: Array<{ id: string; name?: string; translation_key?: string }>;
  onSubmit: () => void;
  onPrev: () => void;
  loading: boolean;
}

const BusinessReviewForm: React.FC<BusinessReviewFormProps> = ({
  formData,
  categories,
  onSubmit,
  onPrev,
  loading
}) => {
  const selectedCategory = categories.find(cat => cat.id === formData.category_id);
  const categoryName = selectedCategory?.name || selectedCategory?.translation_key || 'Unknown Category';

  // Validate required fields
  const validateRequiredFields = () => {
    const missingFields: string[] = [];
    
    if (!formData.name || formData.name.length < 2) missingFields.push('Business name');
    if (!formData.slug || formData.slug.length < 3) missingFields.push('Business URL');
    if (!formData.category_id) missingFields.push('Category');
    if (!formData.description || formData.description.length < 10) missingFields.push('Description');
    if (!formData.contact_email) missingFields.push('Contact email');
    if (!formData.contact_phone) missingFields.push('Contact phone');
    if (!formData.address_line1) missingFields.push('Address');
    if (!formData.city) missingFields.push('City');
    if (!formData.county) missingFields.push('County');
    if (!formData.postal_code) missingFields.push('Postal code');
    if (!formData.country) missingFields.push('Country');
    
    // For paid businesses, discount amount is required
    if (formData.businessType === 'paid' && (!formData.discount_amount || formData.discount_amount <= 0)) {
      missingFields.push('Discount amount');
    }
    
    return missingFields;
  };

  const missingFields = validateRequiredFields();

  const formatAddress = () => {
    const parts = [
      formData.address_line1,
      formData.address_line2,
      formData.city,
      formData.county,
      formData.postal_code,
      formData.country
    ].filter(Boolean);
    
    return parts.join(', ');
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Review & Submit</h2>
        <p className="text-muted-foreground">
          Please review all the information below before submitting your business registration.
        </p>
      </div>

      {/* Validation Summary */}
      {missingFields.length > 0 && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Missing Required Information</AlertTitle>
          <AlertDescription>
            Please complete the following required fields before submitting:
            <ul className="list-disc list-inside mt-2">
              {missingFields.map((field, index) => (
                <li key={index}>{field}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {/* Business Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building className="h-5 w-5" />
            Business Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <h3 className="font-medium text-lg">{formData.name}</h3>
            <Badge variant="secondary">{categoryName}</Badge>
          </div>
          {formData.slug && (
            <div>
              <h4 className="font-medium text-sm text-muted-foreground mb-1">Business URL</h4>
              <span className="inline-block bg-muted px-2 py-1 rounded font-mono font-medium text-foreground/90 text-sm">
                {PATH_CONSTANTS.SERVICE_VIEW.replace(':slug', formData.slug)}
              </span>
            </div>
          )}
          {formData.description && (
            <div>
              <h4 className="font-medium text-sm text-muted-foreground mb-1">Description</h4>
              <p className="text-sm">{formData.description}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{formData.contact_email}</span>
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">{formData.contact_phone}</span>
          </div>
        </CardContent>
      </Card>

      {/* Address */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Address
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm">{formatAddress()}</p>
        </CardContent>
      </Card>

      {/* Opening Hours */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Opening Hours
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm whitespace-pre-line">
            {formatOpeningHours(formData.opening_hours)}
          </div>
        </CardContent>
      </Card>

      {/* Discount Information - Only show for paid businesses */}
      {formData.businessType === 'paid' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5" />
              Discount Offer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <span className="font-medium">
                {formData.discount_amount}
                {formData.is_percentage ? '%' : ''} discount
              </span>
              <span className="text-sm text-muted-foreground ml-2">
                ({formData.applies_to_all ? 'Applies to all services' : 'Applies to select services'})
              </span>
            </div>
            {formData.discount_description && (
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">Description</h4>
                <p className="text-sm">{formData.discount_description}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Business Verification */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Business Verification 
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {formData.website && (
            <div className="flex items-start gap-2">
              <Globe className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <span className="text-sm break-all">{formData.website}</span>
            </div>
          )}
          {formData.facebook_url && (
            <div className="flex items-start gap-2">
              <Facebook className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <span className="text-sm break-all">{formData.facebook_url}</span>
            </div>
          )}
          {formData.instagram_url && (
            <div className="flex items-start gap-2">
              <Instagram className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <span className="text-sm break-all">{formData.instagram_url}</span>
            </div>
          )}
          {formData.tiktok_url && (
            <div className="flex items-start gap-2">
              <svg
                className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
                <path d="M15 8a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                <path d="M15 2v20" />
                <path d="M9 15v7" />
                <path d="M15 13h-3a4 4 0 0 0-4 4" />
              </svg>
              <span className="text-sm break-all">{formData.tiktok_url}</span>
            </div>
          )}
          {formData.google_maps_url && (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Google Maps location provided</span>
            </div>
          )}
          {formData.application_comments && (
            <div>
              <h4 className="font-medium text-sm text-muted-foreground mb-1">Additional Comments</h4>
              <p className="text-sm">{formData.application_comments}</p>
            </div>
          )}
          
          {/* Show placeholder if no verification info provided */}
          {!formData.website && 
           !formData.facebook_url && 
           !formData.instagram_url && 
           !formData.tiktok_url && 
           !formData.google_maps_url && 
           !formData.application_comments && (
            <p className="text-sm text-muted-foreground italic">
              No additional verification information provided
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-between pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={onPrev}
          disabled={loading}
        >
          Previous
        </Button>
        <Button
          type="button"
          onClick={onSubmit}
          disabled={loading || missingFields.length > 0}
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            'Submit Registration'
          )}
        </Button>
      </div>
    </div>
  );
};

export default BusinessReviewForm;
